import type { IAvatarInfo, IIconsVector } from 'shared/ui'
import { formatPhoneNumberNational, formatPhoneNumberToInternational } from 'shared/lib'
import { isIcon } from 'shared/ui/Icon/icons'
import { type IContactIntegrationImplement, type IContactPhone } from 'entities/Contacts'
import type { IIntegrationKey } from 'entities/Integrations'
import type { IParamsCreateContact } from 'entities/Contacts/api/types'

export class ContactIntegrationBase<T extends IContactIntegrationImplement> {
  id: number
  key: number | string
  email = ''
  first_name = ''
  last_name = ''
  national_number = ''
  phone = ''
  mobile_phone = ''
  custom_phone = ''
  photo_url = ''
  integration_type: IIntegrationKey
  origin: T

  constructor(response: T) {
    this.id = response.id as number
    this.key = response.id
    this.first_name = response.first_name ?? ''
    this.last_name = response.last_name ?? ''
    this.integration_type = response.integration_type
    this.origin = response
  }

  get params(): Partial<IParamsCreateContact> {
    return {
      email: this.email,
      first_name: this.first_name || '',
      last_name: this.last_name || '',
      contact_integration_id: String(this.id),
    }
  }

  get integration_vendor_id() {
    return this.id
  }

  get integration_key() {
    return this.integration_type
  }

  get full_name() {
    return `${this.first_name} ${this.last_name}`.trim()
  }

  get color() {
    const palette = ['#6372A5', '#1D95F2', '#10D0AD', '#E13466', '#FFC422', '#FF9423', '#A233E1']

    if (typeof this.key === 'string') {
      let sum = 0
      for (let i = 0; i < this.key.length; i++) {
        sum += this.key.charCodeAt(i)
      }

      return palette[sum % palette.length]
    }

    return palette[this.key % palette.length]
  }

  get avatarInfo(): IAvatarInfo {
    const isPhoneAbbr = !this.first_name && !this.last_name

    return {
      isPhoneAbbr,
      firstName: this.first_name,
      lastName: this.last_name,
      image: this.photo_url,
      color: this.color,
      number: this.number,
    }
  }

  get integrationIcon(): IIconsVector {
    if (isIcon(this.integration_type)) {
      return this.integration_type
    }

    return 'salesmsg'
  }

  get localIcon() {
    return this.integrationIcon
  }

  get number() {
    return formatPhoneNumberToInternational(this.national_number)
  }

  get formatted_number() {
    return formatPhoneNumberNational(this.national_number)
  }

  get phoneInfo() {
    return this.formatted_number || this.national_number || this.number || ''
  }

  get defaultPhoneInfo() {
    return this.formatted_number || this.national_number || this.number
  }

  get phoneCount() {
    return this.phoneNumbers.length
  }

  get phoneNumbers(): IContactPhone[] {
    const phones: IContactPhone[] = []

    if (this.phone) {
      phones.push({
        field: 'Phone',
        type: 'phone',
        number: this.phone,
      })
    }

    if (this.mobile_phone) {
      phones.push({
        field: 'Mobile phone',
        type: 'mobilephone',
        number: this.mobile_phone,
      })
    }

    if (this.custom_phone) {
      phones.push({
        field: 'Custom phone',
        type: 'customphone',
        number: this.custom_phone,
      })
    }

    return phones
  }

  get name() {
    let output = ''
    const full_name = this.full_name.trim()

    if (full_name) {
      output = full_name
    } else if (this.email) {
      output = this.email
    } else if (this.number) {
      output = this.number
    }

    return output ? output : ''
  }
}
